import { default as deserialize } from 'app/utils/deserialize'
import { fetch, create, remove } from 'app/utils/data'
import get from 'lodash/get'
import find from 'lodash/find'
import size from 'lodash/size'
import FormErrors from 'app/utils/form_errors'
import sortBy from 'lodash/sortBy'
import pickBy from 'lodash/pickBy'
import moment from 'moment'
import Vue from 'vue'

export const ICON_UPLOAD_INFINITY = -1

export const PENDING_STATUS = {
  valid: 'valid',
  invalid: 'invalid',
  processing: 'processing',
  uploading: 'uploading'
}

function resolveErrors(error) {
  return get(error, 'response.data.errors', null) || new FormErrors([{ detail: 'PC LOAD LETTER: retry with a baseball bat' }])
}

export default {
  namespaced: true,

  state: {
    list: [],
    item: null,
    pending: {},
    iconUploadCount: 0
  },

  getters: {
    iconUploadCount: (state) => state.iconUploadCount,
    currentIconUploads: (state) => state.list,
    currentIconUpload: (state) => state.item,
    totalUploaded: (_state, _getters, _rootState, rootGetters) => {
      const allKits = rootGetters['kits/currentKits']

      return allKits.reduce((acc, kit) => {
        acc = acc + get(kit, ['iconUploads', 'length'], 0)

        return acc
      }, 0)
    },
    totalUploadedWithPending: (state, getters, _rootState, rootGetters) => {
      return getters['totalUploaded'] + size(state.pending)
    },
    uploadLimit: (_state, _getters, _rootState, rootGetters) => {
      return get(rootGetters['permits/find']('accessToIconUploads'), ['permit', 'iconUploadLimit'], 0)
    },
    infiniteLimit: (_state, getters) => {
      return getters['uploadLimit'] === ICON_UPLOAD_INFINITY
    },
    uploadLimitReached: (_state, getters) => {
      return !getters['infiniteLimit'] && getters['totalUploadedWithPending'] >= getters['uploadLimit']
    },
    uploadLimitExceeded: (_state, getters) => {
      return !getters['infiniteLimit'] && getters['totalUploadedWithPending'] > getters['uploadLimit']
    },
    defaultIconUploadProps: () => {
      return {
        name: '',
        unicode: '',
        version: 1,
        width: 0,
        height: 0,
        path: '',
        pathSecondary: '',
        active: true
      }
    },
    sortedIconUploads: (state) => {
      const val = sortBy(state.list, (k) => moment.utc(k.updatedAt).unix())

      val.reverse()

      return val
    },
    getPending: (state) => (key) => state.pending[key],
    pendingIconUploads: (state) => state.pending,
    validPendingIconUploads: (state) => pickBy(state.pending, { status: PENDING_STATUS.valid }),
    invalidPendingIconUploads: (state) => pickBy(state.pending, { status: PENDING_STATUS.invalid }),
    processingPendingIconUploads: (state) => pickBy(state.pending, { status: PENDING_STATUS.processing }),
    uploadingPendingIconUploads: (state) => pickBy(state.pending, { status: PENDING_STATUS.uploading })
  },

  mutations: {
    setIconUploadCount(state, { count }) {
      state.iconUploadCount = count
    },

    iconUploadsFetched(state, { iconUploads }) {
      state.list = iconUploads
    },

    iconUploadFetched(state, { iconUpload }) {
      state.item = iconUpload

      if (!find(state.list, { id: iconUpload.id })) {
        state.list.push(iconUpload)
      }
    },

    iconUploadDeleted(state, { id }) {
      if (get(state, ['item', 'id']) === id) {
        state.item = null
      }

      state.list = state.list.filter((iconUpload) => {
        return iconUpload.id !== id
      })
    },

    resetState(state) {
      state.list.length = 0
      state.item = null
      state.pending = {}
    },

    setPending(state, pendingIcons = {}) {
      for (const key in pendingIcons) {
        Vue.set(state.pending, key, pendingIcons[key])
      }
    },

    deletePending(state, keyOrKeys = []) {
      if ('string' === typeof keyOrKeys) {
        Vue.delete(state.pending, keyOrKeys)
      } else if (Array.isArray(keyOrKeys)) {
        for (const key of keyOrKeys) {
          Vue.delete(state.pending, key)
        }
      }
    },

    deleteInvalidPending(state) {
      for (const key in state.pending) {
        if (state.pending[key].status === PENDING_STATUS.invalid) {
          Vue.delete(state.pending, key)
        }
      }
    }
  },

  actions: {
    async fetchIconUploadCount({ commit }) {
      try {
        const response = await fetch({ url: '/api/clouds/kits' })
        const kits = await deserialize(response.data)

        const count = kits.reduce((acc, kit) => {
          acc += kit.iconUploads.length
          return acc
        }, 0)

        commit('setIconUploadCount', { count })
      } catch (error) {
        throw new FormErrors(error.response.data.errors)
      }
    },

    async fetchIconUploads({ commit }, kitToken) {
      try {
        const response = await fetch({ url: `/api/clouds/kits/${kitToken}/icon-uploads` })
        const iconUploads = await deserialize(response.data)

        commit('iconUploadsFetched', { iconUploads })
      } catch (error) {
        throw new FormErrors(resolveErrors(error))
      }
    },

    async fetchIconUpload({ commit }, { kitToken, id }) {
      try {
        const response = await fetch({ url: `/api/clouds/kits/${kitToken}/icon-uploads/${id}` })
        const iconUpload = await deserialize(response.data)

        commit('iconUploadFetched', { iconUpload })
      } catch (error) {
        throw new FormErrors(resolveErrors(error))
      }
    },

    async createIconUpload({ commit }, changes) {
      try {
        const response = await create({
          url: `/api/clouds/kits/${changes.kitToken}/icon-uploads`,
          type: 'icon-upload',
          data: changes
        })

        const iconUpload = await deserialize(response.data)

        commit('iconUploadFetched', { iconUpload })

        return iconUpload
      } catch (error) {
        throw new FormErrors(resolveErrors(error))
      }
    },

    async deleteIconUpload({ commit, dispatch }, { kitToken, id }) {
      try {
        await remove({
          url: `/api/clouds/kits/${kitToken}/icon-uploads/${id}`,
          type: 'icon-upload'
        })

        dispatch('kits/fetchKit', kitToken, { root: true })
        commit('iconUploadDeleted', { id })
      } catch (error) {
        throw new FormErrors(resolveErrors(error))
      }
    },

    async uploadIconPackage(_params, kitToken) {
      try {
        await create({
          url: `/api/clouds/kits/${kitToken}/kit-package`,
          type: 'kit-package',
          data: {}
        })
      } catch (error) {
        throw new FormErrors(get(error, 'response.data.errors', [{ detail: 'There was an error uploading your custom icon npm package.' }]))
      }
    }
  }
}
